import { cn, Icon, IconButton } from '@risksmart-app/atomic-ui';

import { ICON_BUTTON_SIZE } from '../types';
import type { ToolbarLabelRevealMode } from '../useObjectToolbarSettingsStore';

interface Props {
  mode: ToolbarLabelRevealMode;
}

/** The dummy action shown inside each preview. */
const PREVIEW_LABEL = 'Add risk';

/**
 * A static, non-interactive illustration of a tray button with its title
 * *forced on* — used inside the Toolbar settings modal so the user can compare
 * the tooltip and slideout treatments without having to hover a real button.
 *
 * This deliberately does not reuse {@link RevealButton}: that component reveals
 * its label on `:hover`/`:focus`, which can't be forced in a preview. Here the
 * revealed state is rendered statically instead.
 */
export const LabelRevealPreview = ({ mode }: Props) => {
  if (mode === 'slideout') {
    // A real slideout tray button frozen in its hovered state: same IconButton
    // (so the dark-grey `bg-neutral-hover` + hover text colour, sizing, padding
    // and rounding are identical), same slideout layout classes as
    // `RevealButton`, but with the reveal grid track and label opacity forced to
    // their open values instead of gated on `:hover`. Keep these class strings
    // in sync with the slideout branch of `RevealButton`.
    return (
      <div className={cn('flex h-16 items-center justify-center')}>
        <IconButton
          aria-hidden={true}
          tabIndex={-1}
          className={cn(
            ICON_BUTTON_SIZE,
            'w-auto min-w-8 justify-end gap-0 overflow-hidden px-1',
            // Freeze the hovered appearance (IconButton's hover:* only applies
            // on real hover; force the same tokens here).
            'pointer-events-none bg-neutral-hover text-neutral-foreground-hover'
          )}
        >
          <span
            className={cn(
              // Resting (tile not hovered): open. On tile hover, loop the reveal
              // width so the label slides open ↔ closed, demonstrating the hover
              // behaviour this option configures.
              'grid grid-cols-[1fr]',
              'group-hover/tile:animate-toolbar-reveal-width'
            )}
            aria-hidden={true}
          >
            {/* Unpadded clipper wrapping the padded label — mirrors RevealButton
                so the px-2 clips to nothing when the loop animates closed. */}
            <span className={cn('overflow-hidden')}>
              <span
                className={cn(
                  'block whitespace-nowrap px-2 text-md',
                  'group-hover/tile:animate-toolbar-reveal-fade'
                )}
              >
                {PREVIEW_LABEL}
              </span>
            </span>
          </span>
          <Icon name={'plus'} size={'lg'} />
        </IconButton>
      </div>
    );
  }

  // Tooltip: the icon with its title shown as a tooltip directly below it.
  return (
    <div className={cn('flex h-16 flex-col items-center justify-center gap-1')}>
      <IconButton
        aria-hidden={true}
        tabIndex={-1}
        className={cn(ICON_BUTTON_SIZE, 'pointer-events-none')}
      >
        <Icon name={'plus'} size={'lg'} />
      </IconButton>
      <span
        className={cn(
          'whitespace-nowrap rounded-md border border-neutral-border-minimal',
          'bg-neutral px-2 py-1 text-base text-neutral-foreground shadow-sm',
          // Loop the tooltip fade in ↔ out while the tile is hovered.
          'group-hover/tile:animate-toolbar-reveal-fade'
        )}
      >
        {PREVIEW_LABEL}
      </span>
    </div>
  );
};
